/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Parallax, IParallax } from "@react-spring/parallax";
import SunPage from "./pages/sunhome/SunHome";
import ProjectBanner from "./banner/ProjectsBanner";
import AboutBonkPage from "./pages/aboutbonk/AboutBonk";
import FamilyCarousel from "./banner/FamilyBanner";
import IntegrationPage from "./pages/integration/Integration";
import ChatBotBanner from "./banner/ChatBox";
import NewsLetterPage from "./pages/newsletter/NewsLetter";
import ContactBanner from "./banner/ContactBanner";
import ComsPage from "./pages/coms/Coms";
import NavigateMenu from "./ui/NavigateMenu";
import AgreementModal from "./ui/AgreementModal";
import { useSunScrollUnlock } from "../context/SunScrollContext";

import { PageNavigationProvider } from "../context/pageNavigation.context";
import "./MainScroll.css";

const MainScrollContent: React.FC = () => {
	const parallaxRef = useRef<IParallax | null>(null);
	const pageRefs = useRef<Array<HTMLDivElement | null>>([]);

	const [visitedSteps, setVisitedSteps] = useState<number[]>([0]);
	const [maxScroll, setMaxScroll] = useState<number>(0);
	const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
	const [hasAgreed, setHasAgreed] = useState(() => localStorage.getItem("agreementAccepted") === "true");

	const bannerOffset = 0.2;
	const { isScrollEnabled } = useSunScrollUnlock();

	// Recalculate maxScroll whenever window.innerHeight changes
	useEffect(() => {
		const calculateMax = () => {
			const lastStep = 4 + 4 * bannerOffset;
			const newMax = lastStep * window.innerHeight;
			setMaxScroll(newMax);

			const container = document.querySelector(".parallax-container") as HTMLDivElement | null;
			if (container && container.scrollTop > newMax) {
				container.scrollTop = newMax;
			}
		};

		calculateMax();
		window.addEventListener("resize", calculateMax);
		return () => window.removeEventListener("resize", calculateMax);
	}, []);

	const markVisited = useCallback((stepValue: number) => {
		setVisitedSteps((prev) => (prev.includes(stepValue) ? prev : [...prev, stepValue]));
	}, []);

	const steps = useMemo(() => {
		return [
			0,
			0.6,
			1 + bannerOffset,
			1.4 + 2 * bannerOffset,
			2 + 2 * bannerOffset,
			2.4 + 3 * bannerOffset,
			3 + 3 * bannerOffset,
			3.4 + 4 * bannerOffset,
			4 + 4 * bannerOffset,
		];
	}, []);

	// Scroll tracking && overscroll prevention
	useEffect(() => {
		const container = document.querySelector(".parallax-container") as HTMLDivElement | null;
		if (!container) return;

		const handleScroll = () => {
			const scrollTop = container.scrollTop;
			pageRefs.current.forEach((page, i) => {
				if (!page) return;
				const trigger = steps[i] * window.innerHeight;
				if (scrollTop >= trigger - 200) markVisited(steps[i]);
			});
		};

		const preventOverScroll = (e: WheelEvent) => {
			if ((e.target as HTMLElement).closest(".privacy-modal-content")) return;

			const scrollTop = container.scrollTop;
			const epsilon = 2;
			const bottom = steps[8] * window.innerHeight;

			// don't scroll past bottom
			if (scrollTop >= maxScroll - epsilon && e.deltaY > 0) {
				e.preventDefault();
				container.scrollTop = maxScroll;
			}
			// prevent scroll-up through the bottom of the privacy modal
			if (scrollTop <= bottom + epsilon && e.deltaY < 0 && isPrivacyOpen) {
				e.preventDefault();
				container.scrollTop = bottom;
			}
		};

		container.addEventListener("scroll", handleScroll);
		container.addEventListener("wheel", preventOverScroll, { passive: false });
		return () => {
			container.removeEventListener("scroll", handleScroll);
			container.removeEventListener("wheel", preventOverScroll);
		};
	}, [markVisited, steps, maxScroll, isPrivacyOpen]);

	const scrollToPage = (idx: number) => parallaxRef.current?.scrollTo(steps[idx]);
	return (
		<>
			<PageNavigationProvider navigate={scrollToPage}>
				{isScrollEnabled && <NavigateMenu onNavigate={(pageIndex) => parallaxRef.current?.scrollTo(steps[pageIndex])} />}

				<Parallax
					ref={parallaxRef}
					pages={6 - bannerOffset}
					config={{ tension: 350, friction: 40 }}
					style={{ overflowY: "auto", height: "100vh", width: "100vw" }}
					className="parallax-container"
				>
					{[SunPage, ProjectBanner, AboutBonkPage, FamilyCarousel, IntegrationPage, ChatBotBanner, NewsLetterPage, ContactBanner, ComsPage].map(
						(PageComponent, idx) => (
							<div
								key={idx}
								ref={(el) => {
									pageRefs.current[idx] = el;
								}}
								data-step={steps[idx]}
								style={{ minHeight: "100vh" }}
							>
								{idx === 0 && (
									<SunPage
										offset={0}
										hasAgreed={hasAgreed}
									/>
								)}
								{idx === 1 && (
									<ProjectBanner
										offset={1}
										isFirstVisit={visitedSteps.includes(steps[1])}
									/>
								)}
								{idx === 2 && (
									<AboutBonkPage
										offset={1 + bannerOffset}
										isFirstVisit={visitedSteps.includes(steps[2])}
									/>
								)}
								{idx === 3 && (
									<FamilyCarousel
										offset={2 + bannerOffset}
										isFirstVisit={visitedSteps.includes(steps[3])}
									/>
								)}
								{idx === 4 && (
									<IntegrationPage
										offset={2 + 2 * bannerOffset}
										isFirstVisit={visitedSteps.includes(steps[4])}
									/>
								)}
								{idx === 5 && <ChatBotBanner offset={2 + 3 * bannerOffset} />}
								{idx === 6 && <NewsLetterPage offset={3 + 3 * bannerOffset} />}
								{idx === 7 && <ContactBanner offset={3 + 4 * bannerOffset} />}
								{idx === 8 && (
									<ComsPage
										offset={4 + 4 * bannerOffset}
										isFirstVisit={visitedSteps.includes(steps[8])}
										onPrivacyOpenChange={setIsPrivacyOpen}
									/>
								)}
							</div>
						)
					)}
				</Parallax>

				<AgreementModal onAgree={() => setHasAgreed(true)} />
			</PageNavigationProvider>
		</>
	);
};

export default MainScrollContent;
